import { api, ApiResponse, PaginationMeta, buildQueryParams, validateRequiredParams, formatApiError } from './api';

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserData {
  email: string;
  password: string;
  name: string;
  role?: string;
}

export interface UpdateUserData {
  email?: string;
  name?: string;
  role?: string;
}

export interface UserFilters {
  role?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface UsersResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: PaginationMeta;
  };
}

export interface UserStatsResponse {
  success: boolean;
  data: {
    overview: {
      totalUsers: number;
      activeUsers: number;
      pendingUsers: number;
      suspendedUsers: number;
    };
    roleDistribution: Record<string, number>;
  };
}

export const userApi = {
  // Create a new user
  createUser: async (data: CreateUserData): Promise<ApiResponse<{ user: User }>> => {
    try {
      validateRequiredParams(data, ['name', 'email', 'password']);
      const response = await api.post('/users', data);
      return response.data;
    } catch (error: any) {
      const apiError = formatApiError(error);
      throw new Error(apiError.message);
    }
  },

  // Get all users with filters
  getUsers: async (filters: UserFilters = {}): Promise<UsersResponse> => {
    try {
      const queryString = buildQueryParams(filters);
      const url = queryString ? `/users?${queryString}` : '/users';
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      const apiError = formatApiError(error);
      throw new Error(apiError.message);
    }
  },

  // Get user by ID
  getUserById: async (userId: string): Promise<ApiResponse<{ user: User }>> => {
    try {
      validateRequiredParams({ userId }, ['userId']);
      const response = await api.get(`/users/${encodeURIComponent(userId)}`);
      return response.data;
    } catch (error: any) {
      const apiError = formatApiError(error);
      throw new Error(apiError.message);
    }
  },

  // Update user
  updateUser: async (userId: string, data: UpdateUserData): Promise<ApiResponse<{ user: User }>> => {
    try {
      validateRequiredParams({ userId }, ['userId']);
      const response = await api.put(`/users/${encodeURIComponent(userId)}`, data);
      return response.data;
    } catch (error: any) {
      const apiError = formatApiError(error);
      throw new Error(apiError.message);
    }
  },

  // Delete user
  deleteUser: async (userId: string): Promise<ApiResponse> => {
    try {
      validateRequiredParams({ userId }, ['userId']);
      const response = await api.delete(`/users/${encodeURIComponent(userId)}`);
      return response.data;
    } catch (error: any) {
      const apiError = formatApiError(error);
      throw new Error(apiError.message);
    }
  },

  // Get users by role
  getUsersByRole: async (role: string): Promise<ApiResponse<{ users: User[] }>> => {
    try {
      validateRequiredParams({ role }, ['role']);
      const response = await api.get(`/users/role/${encodeURIComponent(role)}`);
      return response.data;
    } catch (error: any) {
      const apiError = formatApiError(error);
      throw new Error(apiError.message);
    }
  },

  // Get user statistics
  getUserStats: async (): Promise<UserStatsResponse> => {
    try {
      const response = await api.get('/users/stats');
      return response.data;
    } catch (error: any) {
      const apiError = formatApiError(error);
      throw new Error(apiError.message);
    }
  },

  // Bulk update users
  bulkUpdateUsers: async (userIds: string[], updateData: Partial<UpdateUserData>): Promise<ApiResponse> => {
    try {
      validateRequiredParams({ userIds }, ['userIds']);
      if (!Array.isArray(userIds) || userIds.length === 0) {
        throw new Error('userIds must be a non-empty array');
      }
      const response = await api.patch('/users/bulk', { userIds, updateData });
      return response.data;
    } catch (error: any) {
      const apiError = formatApiError(error);
      throw new Error(apiError.message);
    }
  },

  // Search users
  searchUsers: async (query: string, limit: number = 10): Promise<ApiResponse<{ users: User[] }>> => {
    try {
      validateRequiredParams({ query }, ['query']);
      const queryString = buildQueryParams({ q: query, limit });
      const response = await api.get(`/users/search?${queryString}`);
      return response.data;
    } catch (error: any) {
      const apiError = formatApiError(error);
      throw new Error(apiError.message);
    }
  },
};

// User roles constants (matching backend)
export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MANAGER: 'manager',
  USER: 'user',
} as const;

export const ROLE_LABELS = {
  [USER_ROLES.SUPER_ADMIN]: 'Super Admin',
  [USER_ROLES.ADMIN]: 'Admin',
  [USER_ROLES.MANAGER]: 'Manager',
  [USER_ROLES.USER]: 'User',
};

// User status constants
export const USER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  SUSPENDED: 'suspended',
} as const;

export const STATUS_LABELS = {
  [USER_STATUS.ACTIVE]: 'Active',
  [USER_STATUS.INACTIVE]: 'Inactive',
  [USER_STATUS.PENDING]: 'Pending',
  [USER_STATUS.SUSPENDED]: 'Suspended',
};